import { useState } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Check, Crown, Star, Zap } from 'lucide-react'
import { useSubscription } from '@/hooks/useSubscription'
import { toast } from 'sonner'

const plans = [
  {
    id: 'basic',
    name: 'Basic',
    price: '€9.99',
    priceId: 'price_1QgLCKCNiZl7NwFmBvb2zGCQ',
    description: 'Perfekt für kleine Unternehmen',
    icon: <Check className="h-6 w-6 text-blue-500" />,
    features: [
      '100 Kunden',
      '1000 Stempel pro Monat',
      '50 Empfehlungen pro Monat',
      'Standard Support',
      'Basis Analytics'
    ]
  },
  {
    id: 'pro',
    name: 'Pro',
    price: '€19.99',
    priceId: 'price_1QgLCrCNiZl7NwFmTFZ6M3H1',
    description: 'Ideal für wachsende Unternehmen',
    icon: <Star className="h-6 w-6 text-purple-500" />,
    popular: true,
    features: [
      '500 Kunden',
      '5000 Stempel pro Monat',
      '200 Empfehlungen pro Monat',
      'Priority Support',
      'Erweiterte Analytics',
      'Custom Branding',
      'API Zugang'
    ]
  },
  {
    id: 'enterprise',
    name: 'Enterprise',
    price: '€49.99',
    priceId: 'price_1QgLD8CNiZl7NwFmLbV7LT8S',
    description: 'Für große Unternehmen',
    icon: <Crown className="h-6 w-6 text-gold-500" />,
    features: [
      'Unbegrenzte Kunden',
      'Unbegrenzte Stempel',
      'Unbegrenzte Empfehlungen',
      'Dedicated Support',
      'Premium Analytics',
      'White Label',
      'Custom Integrationen',
      'SLA Garantie'
    ]
  }
]

export const SubscriptionPlans = () => {
  const { subscriptionStatus, createCheckout, openCustomerPortal, getCurrentPlan } = useSubscription()
  const [loading, setLoading] = useState<string | null>(null)
  const currentPlan = getCurrentPlan()

  const handleSubscribe = async (plan: any) => {
    if (subscriptionStatus.subscribed && currentPlan?.id === plan.id) {
      toast.info('Sie haben bereits diesen Plan abonniert')
      return
    }

    setLoading(plan.id)
    try {
      await createCheckout(plan.id)
    } catch (error: any) {
      toast.error('Fehler beim Erstellen des Checkouts: ' + error.message)
    } finally {
      setLoading(null)
    }
  }

  const handleManageSubscription = async () => {
    try {
      await openCustomerPortal()
    } catch (error: any) {
      toast.error('Fehler beim Öffnen des Kundenportals: ' + error.message)
    }
  }

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="text-3xl font-bold">Abonnement-Pläne</h2>
        <p className="text-muted-foreground mt-2">
          Wählen Sie den perfekten Plan für Ihr Unternehmen
        </p>
        {subscriptionStatus.subscribed && (
          <div className="mt-4">
            <Badge variant="secondary" className="text-lg py-2 px-4">
              Aktueller Plan: {currentPlan?.name || 'Unbekannt'}
            </Badge>
            <div className="mt-2">
              <Button onClick={handleManageSubscription} variant="outline">
                Abonnement verwalten
              </Button>
            </div>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {plans.map((plan) => {
          const isCurrentPlan = subscriptionStatus.subscribed && currentPlan?.id === plan.id
          const isLoading = loading === plan.id

          return (
            <Card 
              key={plan.id} 
              className={`relative ${plan.popular ? 'border-primary ring-2 ring-primary/20' : ''} ${isCurrentPlan ? 'border-green-500 ring-2 ring-green-500/20' : ''}`}
            >
              {plan.popular && (
                <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                  <Badge className="bg-primary text-primary-foreground">
                    <Zap className="h-3 w-3 mr-1" />
                    Beliebt
                  </Badge>
                </div>
              )}

              {isCurrentPlan && (
                <div className="absolute -top-3 right-4">
                  <Badge className="bg-green-500 text-white">
                    Ihr Plan
                  </Badge>
                </div>
              )}

              <CardHeader className="text-center">
                <div className="flex justify-center mb-4">
                  {plan.icon}
                </div>
                <CardTitle className="text-xl">{plan.name}</CardTitle>
                <div className="text-3xl font-bold">{plan.price}</div>
                <CardDescription>{plan.description}</CardDescription>
              </CardHeader>

              <CardContent className="space-y-4">
                <ul className="space-y-2">
                  {plan.features.map((feature, index) => (
                    <li key={index} className="flex items-center">
                      <Check className="h-4 w-4 text-green-500 mr-2" />
                      <span className="text-sm">{feature}</span>
                    </li>
                  ))}
                </ul>

                <Button 
                  className="w-full" 
                  onClick={() => handleSubscribe(plan)}
                  disabled={isLoading || subscriptionStatus.loading}
                  variant={isCurrentPlan ? "outline" : "default"}
                >
                  {isLoading ? (
                    'Wird geladen...'
                  ) : isCurrentPlan ? (
                    'Aktueller Plan'
                  ) : subscriptionStatus.subscribed ? (
                    'Plan wechseln'
                  ) : (
                    'Jetzt abonnieren'
                  )}
                </Button>
              </CardContent>
            </Card>
          )
        })}
      </div>

      {!subscriptionStatus.subscribed && (
        <div className="text-center text-muted-foreground">
          <p>Alle Pläne beinhalten eine 14-tägige kostenlose Testversion</p>
          <p className="text-sm mt-1">Jederzeit kündbar • Keine versteckten Kosten</p>
        </div>
      )}
    </div>
  )
}